import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import Switch from '@components/v2/form/Switch/Switch';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import * as R from 'ramda';
import LangEnum from '../../../../lang/LangEnum';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import CopyOrderWithConditions from '../../components/CopyOrderWithConditions';
import OrderGridSettings from '../../components/OrderGridSettings';
import { useCopyOrderModalStyles } from '../styles/copyOrderModal.styles';
import { CopyOrderContentParams, CustomerToCopy } from '../types/copyOrderModal.types';
import { CustomerOptionsCopyOrderRenderer } from './CustomerOptionsCopyOrderRenderer';

const getCopyAsDraftSubtitle = (isSeller: boolean) => {
  return isSeller
    ? LangEnum.SUGGESTED_ORDER_COPY_SUBTITLE_DRAFT_SELLER
    : LangEnum.SUGGESTED_ORDER_COPY_SUBTITLE_DRAFT;
};

const getCustomerOptionLabel = (customer: CustomerToCopy) =>
  `${customer.commercialName} (${R.isNil(customer.code) ? '-' : customer.code})`;

const CopyOrderContent = ({
  copyAsDraft,
  copyOrderWithConditions,
  disableCopyOrderWithConditions,
  customers,
  setCustomers,
  source,
  onChangeInsertGridMultiplier,
  onChangeCopyOrderWithConditions,
  invalidCustomerIds,
  isBatchMode,
  keepOriginalCustomer,
  onChangeKeepOriginalCustomer,
}: CopyOrderContentParams) => {
  const { classes } = useCopyOrderModalStyles();
  const { translate } = useTranslation();
  const { isStore, isSeller } = useApplicationContext();

  const hasCustomersWithError = customers.some((customer) =>
    invalidCustomerIds?.includes(customer.id),
  );

  return (
    <Stack sx={{ gap: 2 }}>
      <Stack sx={{ gap: 2, pl: 0.5 }}>
        <Typography variant="overline" color="text.secondary">
          {copyAsDraft
            ? translate(LangEnum.SUGGESTED_ORDER_COPY_MESSAGE_DRAFT)
            : translate(LangEnum.SUGGESTED_ORDER_COPY_MESSAGE_SUGGESTED)}
        </Typography>
        <Typography color="text.secondary">
          {copyAsDraft
            ? translate(getCopyAsDraftSubtitle(isSeller))
            : translate(LangEnum.SUGGESTED_ORDER_COPY_SUBTITLE_SUGGESTED)}
        </Typography>
      </Stack>
      <Stack sx={{ pt: 0.5 }}>
        <AsyncAutocomplete<CustomerToCopy, true>
          multiple
          label={translate(LangEnum.ORDER_COPY_DIALOG_INPUT_LABEL)}
          value={customers.map((customer) => customer.id)}
          initialData={customers}
          readOnly={isStore}
          disabled={keepOriginalCustomer}
          error={hasCustomersWithError}
          source={source}
          getOptionLabel={getCustomerOptionLabel}
          helperText={
            hasCustomersWithError
              ? translate(LangEnum.SUGGESTED_ORDER_COPY_INVALID_CUSTOMERS_HELPER_TEXT)
              : undefined
          }
          getChipConfig={(customerId) =>
            invalidCustomerIds?.includes(customerId)
              ? { className: classes.invalidChip }
              : undefined
          }
          renderOption={(customer) => <CustomerOptionsCopyOrderRenderer customer={customer} />}
          onChange={(_, selectedCustomers) => setCustomers(selectedCustomers)}
        />
        {isBatchMode && !isStore && (
          <Switch
            label={translate(LangEnum.COPY_ORDER_KEEP_ORIGINAL_CUSTOMER_LABEL)}
            checked={!!keepOriginalCustomer}
            sx={{ ml: 0 }}
            onChange={(_, checked) => onChangeKeepOriginalCustomer?.(checked)}
          />
        )}
      </Stack>
      <Stack sx={{ gap: 1, pl: 0.75 }}>
        <OrderGridSettings onChangeInsertGridMultiplier={onChangeInsertGridMultiplier} />
        <CopyOrderWithConditions
          copyOrderWithConditions={copyOrderWithConditions}
          disabled={disableCopyOrderWithConditions}
          onChangeCopyOrderWithConditions={onChangeCopyOrderWithConditions}
        />
      </Stack>
    </Stack>
  );
};

export default CopyOrderContent;
